/**
 * Regex list + sandbox for a Monitoring System's parsing definitions.
 *
 * Three blocks, in the order the work happens. The regex list, which is what gets saved. A builder that turns a real
 * message into a regex: load the e-mails this mailbox received or paste one, highlight the equipment, the severity
 * token and the state token, and the regex is generated from the labels around the highlights. Then a test, which
 * runs the definitions through the MEP endpoint that uses exactly the production parser and reports per e-mail what
 * would happen: an alert with its source, severity and state, an ignored e-mail, or no match at all. Both the builder and
 * the test work on the same pool of messages. Nothing is saved until the editor's Save button.
 */
import {
  buildRegexFromSpans,
  normalizeForRegex,
  Parser,
  ParserSandboxOutcome,
  ParserSandboxResult,
  RawSample,
  RegexTag,
  TaggedSpan,
} from '@serverfarm/nocd-commons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Icon from 'react-bootstrap-icons';
import { Alert as BsAlert, Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Table } from 'reactstrap';

import config from '../../config';
import axios from '../../services/axios';

import { withParserDefaults } from './parserDefaults';

const SEVERITY_LABEL: { [level: number]: string } = { 0: 'UNKNOWN', 1: 'HIGHEST', 2: 'HIGH', 3: 'MEDIUM', 4: 'LOW', 5: 'LOWEST' };
const TAG_COLORS: { [tag in RegexTag]: string } = { source: '#cfe2ff', severity: '#ffe5b4', state: '#d1f0d1' };
const PENDING_COLOR = '#fff3cd';

interface Props {
  monitoringSystemId?: string;
  site?: string;
  type?: string;
  /** Optional on purpose: a system written outside NOCD has no parser subdocument. See parserDefaults. */
  parser?: Parser;
  onRegexChange: (regex: string[]) => void;
}

interface Pending {
  start: number;
  end: number;
  text: string;
}

interface BuilderState {
  examples: { [messageIndex: number]: TaggedSpan[] };
  pending: Pending | null;
}

const api = config.services.crawfish.api.url;

const renderHighlighted = (message: string, spans: Array<{ start: number; end: number; color: string; title: string }>): React.ReactNode => {
  const sorted = spans.slice().sort((a, b) => a.start - b.start);
  const out: React.ReactNode[] = [];
  let pos = 0;
  sorted.forEach((s, i) => {
    if (s.start < pos) {
      return;
    }
    out.push(message.slice(pos, s.start));
    out.push(
      <mark key={i} title={s.title} style={{ background: s.color, padding: '0 2px', borderRadius: 2 }}>
        {message.slice(s.start, s.end)}
      </mark>,
    );
    pos = s.end;
  });
  out.push(message.slice(pos));
  return out;
};

/** Highlight what the regex captured, by locating the captured values in the normalised message. */
const capturedSpans = (outcome: ParserSandboxOutcome): Array<{ start: number; end: number; color: string; title: string }> => {
  if (outcome.kind !== 'parsed' || !outcome.groups) {
    return [];
  }
  const spans: Array<{ start: number; end: number; color: string; title: string }> = [];
  (['source', 'severity', 'state'] as RegexTag[]).forEach((tag) => {
    const value = outcome.groups && outcome.groups[tag];
    if (!value) {
      return;
    }
    const start = outcome.message.indexOf(value);
    if (start >= 0) {
      spans.push({ start, end: start + value.length, color: TAG_COLORS[tag], title: tag });
    }
  });
  return spans;
};

export const ParsingSandbox: React.FC<Props> = ({ monitoringSystemId, site, type, parser: givenParser, onRegexChange }) => {
  /**
   * Memoised on the prop, not rebuilt per render: `withParserDefaults` returns a fresh object, and the regex memo
   * below keys on `parser.regex`, so an unstable identity would put the sandbox back in the request loop.
   */
  const parser = useMemo(() => withParserDefaults(givenParser), [givenParser]);
  const [samples, setSamples] = useState<RawSample[]>([]);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const [mailboxCode, setMailboxCode] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [result, setResult] = useState<ParserSandboxResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinned, setPinned] = useState(0);
  const [builder, setBuilder] = useState<BuilderState | null>({ examples: {}, pending: null });
  const [copiedRegex, setCopiedRegex] = useState<number | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [pastedRegex, setPastedRegex] = useState('');
  const [addedRegexAt, setAddedRegexAt] = useState<number | null>(null);
  const [sourceMode, setSourceMode] = useState<'received' | 'pasted'>('received');
  const [editingPaste, setEditingPaste] = useState(true);
  const textRef = useRef<HTMLDivElement>(null);

  /**
   * Memoised on purpose. `parser.regex || []` built a new empty array on every render, which changed the identity the
   * sandbox effect depends on, which set state, which rendered again: a system with no regex called the sandbox
   * endpoint in a loop for as long as the test was open.
   */
  const regex = useMemo(() => parser.regex || [], [parser.regex]);
  /**
   * The messages both panels work on come from one source at a time, whichever the toggle has selected. A pasted
   * e-mail is one message even though it spans several lines, so blocks are separated by a blank line: splitting on
   * every newline turned a single e-mail into three unrelated fragments.
   */
  const messages = useMemo(
    () =>
      sourceMode === 'received'
        ? samples.map((s) => s.message)
        : pasted
            .split(/\n\s*\n/)
            .map((block) => block.trim())
            .filter(Boolean),
    [samples, pasted, sourceMode],
  );

  const loadSamples = useCallback(() => {
    if (!monitoringSystemId) {
      return;
    }
    setLoadingSamples(true);
    setError(null);
    axios
      .get(`${api}/monitoring-systems/${monitoringSystemId}/raw-samples`, { params: { limit: 50 } })
      .then((res) => {
        const payload = res.data || {};
        /*
         * A 200 without a `messages` array is not an empty mailbox, it is an answer we did not expect: crawfish passes
         * the MEP body through untouched, so a MEP error arrives here as a 200 with `msg` and no list. Saying "nothing
         * reached this mailbox" in that case sends whoever reads it looking in the wrong place.
         */
        if (!Array.isArray(payload.messages)) {
          setSamples([]);
          setLoadedCount(null);
          setError(`The API did not return a list of e-mails. It answered: ${JSON.stringify(payload).slice(0, 300)}`);
          return;
        }
        setSamples(payload.messages);
        setLoadedCount(payload.messages.length);
        setMailboxCode(payload.code || null);
      })
      .catch((e) => {
        setLoadedCount(null);
        setError(`Could not load recent e-mails: ${e.message}`);
      })
      .finally(() => setLoadingSamples(false));
  }, [monitoringSystemId]);

  // Keep the message being viewed inside the list when the source changes under it.
  useEffect(() => {
    setPinned((current) => (current < messages.length ? current : 0));
  }, [messages.length]);

  /**
   * Everything the sandbox call depends on, as one string. Comparing the payload itself compares object identities,
   * which churn on every render of the editor above; comparing its JSON compares what actually changed.
   */
  const sandboxKey = useMemo(() => {
    const { timezone, ...definitions } = parser as Parser & { timezone?: string };
    void timezone;
    return JSON.stringify({ parser: { ...definitions, regex }, messages });
  }, [parser, regex, messages]);

  // Once the test is open, re-run it whenever the definitions or the messages change (debounced), so editing a regex
  // updates the outcome without pressing the button again. Before that it stays quiet and calls nothing.
  useEffect(() => {
    if (!messages.length || !showTest) {
      setResult(null);
      return;
    }
    const handle = setTimeout(() => {
      setRunning(true);
      axios
        .post(`${api}/monitoring-systems/parser/sandbox`, JSON.parse(sandboxKey))
        .then((res) => {
          setResult(res.data as ParserSandboxResult);
          setError(null);
        })
        .catch((e) => setError(`Sandbox failed: ${e.message}`))
        .finally(() => setRunning(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [sandboxKey, showTest, messages.length]);

  // ---- regex list -------------------------------------------------------------------------------------------------
  const updateRegex = (index: number, value: string) => onRegexChange(regex.map((r, i) => (i === index ? value : r)));
  const removeRegex = (index: number) => onRegexChange(regex.filter((_, i) => i !== index));
  const moveRegex = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= regex.length) {
      return;
    }
    const next = regex.slice();
    [next[index], next[j]] = [next[j], next[index]];
    onRegexChange(next);
  };
  const matchedBy = (index: number): number => (result ? result.results.filter((r) => r.kind === 'parsed' && r.matchedRegex === index).length : 0);

  // ---- builder ----------------------------------------------------------------------------------------------------
  const draft = useMemo(() => {
    if (!builder) {
      return null;
    }
    const examples = Object.entries(builder.examples)
      .filter(([, spans]) => spans.length)
      .map(([idx, spans]) => ({ message: normalizeForRegex(messages[Number(idx)] || ''), spans }));
    return examples.length ? buildRegexFromSpans(examples) : null;
  }, [builder, messages]);

  /**
   * A regex is only worth adding when it tags all three parts. One that captures the source alone leaves the severity
   * and the state to be searched in the whole e-mail, which is the loose matching this work replaces, so the builder
   * refuses to hand it over until every example carries source, severity and state.
   */
  const missingDraftTags = useMemo(() => {
    if (!builder) {
      return ['source', 'severity', 'state'];
    }
    const tagged = Object.values(builder.examples).filter((spans) => spans.length);
    if (!tagged.length) {
      return ['source', 'severity', 'state'];
    }
    return (['source', 'severity', 'state'] as RegexTag[]).filter((tag) => tagged.some((spans) => !spans.some((s) => s.tag === tag)));
  }, [builder]);

  /** What to ask for next, named the way someone reading the e-mail would name it. */
  const nextTagToSelect = useMemo(() => {
    const names: { [tag: string]: string } = { source: 'equipment name', severity: 'severity token', state: 'state token' };
    return names[missingDraftTags[0]] || 'part you need';
  }, [missingDraftTags]);

  const draftMatches = useMemo(() => {
    if (!draft || !draft.regex || draft.problems.length) {
      return null;
    }
    try {
      const re = new RegExp(draft.regex, 'i');
      return messages.map((m, i) => (re.test(normalizeForRegex(m)) ? i : -1)).filter((i) => i >= 0);
    } catch (e) {
      return null;
    }
  }, [draft, messages]);

  const onMouseUp = () => {
    if (!builder || !textRef.current) {
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }
    const range = selection.getRangeAt(0);
    const container = textRef.current;
    if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
      return;
    }
    const before = range.cloneRange();
    before.selectNodeContents(container);
    before.setEnd(range.startContainer, range.startOffset);
    let start = before.toString().length;
    let text = range.toString();
    start += text.length - text.replace(/^\s+/, '').length;
    text = text.trim();
    if (!text) {
      return;
    }
    selection.removeAllRanges();
    setBuilder({ ...builder, pending: { start, end: start + text.length, text } });
  };

  const tagPending = (tag: RegexTag) => {
    if (!builder || !builder.pending) {
      return;
    }
    const p = builder.pending;
    const current = (builder.examples[pinned] || []).filter((s) => s.tag !== tag && !(s.start < p.end && p.start < s.end));
    setBuilder({ examples: { ...builder.examples, [pinned]: [...current, { tag, start: p.start, end: p.end }] }, pending: null });
  };
  const untag = (tag: RegexTag) => {
    if (!builder) {
      return;
    }
    const remaining = (builder.examples[pinned] || []).filter((s) => s.tag !== tag);
    const examples = { ...builder.examples };
    if (remaining.length) {
      examples[pinned] = remaining;
    } else {
      delete examples[pinned];
    }
    setBuilder({ ...builder, examples });
  };
  const addDraft = () => {
    if (draft && draft.regex && !draft.problems.length) {
      onRegexChange([...regex, draft.regex]);
      // The panel stays open with its tags cleared, ready for the next message that no regex covers yet.
      setBuilder({ examples: {}, pending: null });
    }
  };

  /**
   * Which regex already in the list, if any, matches the message on screen. Building a second regex for a message that
   * is already covered adds a rule that will never be reached, so the builder says so before the work is done.
   */
  const existingMatch = useMemo(() => {
    const normalized = normalizeForRegex(messages[pinned] || '');
    if (!normalized) {
      return -1;
    }
    for (let i = 0; i < regex.length; i += 1) {
      try {
        if (regex[i] && new RegExp(regex[i], 'i').test(normalized)) {
          return i;
        }
      } catch (e) {
        // An unfinished regex in the list is not a reason to stop; the test reports it.
      }
    }
    return -1;
  }, [messages, pinned, regex]);

  const pinnedMessage = normalizeForRegex(messages[pinned] || '');
  const pinnedSpans = builder ? builder.examples[pinned] || [] : [];
  const exampleIndexes = builder ? Object.keys(builder.examples).map(Number) : [];

  /** One row per e-mail. A failure shows the e-mail itself, which is the part worth reading when nothing matched. */
  const outcomeRow = (r: ParserSandboxOutcome, i: number) => {
    const base = { cursor: 'pointer', fontWeight: i === pinned ? 600 : 400 } as React.CSSProperties;
    const excerpt = (text: string) => (text.length > 110 ? `${text.slice(0, 110)}…` : text);
    const untaggedTitle = (tagged: boolean | undefined, what: string) =>
      tagged ? `read from the ${what} the regex tagged` : `the regex tagged no ${what}, so the keywords were searched in the whole e-mail`;

    if (r.kind === 'ignored') {
      return (
        <tr key={i} style={base} onClick={() => setPinned(i)}>
          <td className="align-middle">{i + 1}</td>
          <td className="align-middle">
            <Badge color="secondary">ignored</Badge>
          </td>
          <td colSpan={3} className="text-muted">
            Ignore keyword <code>{r.keyword}</code>, no alert
          </td>
        </tr>
      );
    }

    if (r.kind === 'failed') {
      return (
        <tr key={i} className="table-danger" style={base} onClick={() => setPinned(i)}>
          <td className="align-middle">{i + 1}</td>
          <td className="align-middle">
            <Badge color="danger">no match</Badge>
          </td>
          <td colSpan={3} className="text-monospace text-muted" style={{ fontSize: 11 }}>
            {excerpt(r.message)}
          </td>
        </tr>
      );
    }

    const isDraft = r.matchedRegex != null && r.matchedRegex >= regex.length;
    return (
      <tr key={i} className={i === pinned ? 'table-active' : ''} style={base} onClick={() => setPinned(i)}>
        <td className="align-middle">{i + 1}</td>
        <td className="align-middle">
          {r.matchedRegex == null ? (
            <Badge color="light" className="border">
              no regex
            </Badge>
          ) : isDraft ? (
            <Badge color="info">draft</Badge>
          ) : (
            <Badge color="light" className="border">
              regex {r.matchedRegex + 1}
            </Badge>
          )}
        </td>
        <td className="text-monospace align-middle" style={{ fontSize: 12, wordBreak: 'break-word' }}>
          {r.source}
        </td>
        <td className="align-middle">
          <Badge
            color={
              r.severityLevel === 1 || r.severityLevel === 2
                ? 'danger'
                : r.severityLevel === 3
                ? 'warning'
                : r.severityLevel === 0
                ? 'secondary'
                : 'info'
            }
            title={untaggedTitle(r.tagged && r.tagged.severity, 'severity')}
          >
            {r.severityLevel} {SEVERITY_LABEL[r.severityLevel || 0]}
          </Badge>
        </td>
        <td className="align-middle">
          <Badge color={r.state === 'NORMAL' ? 'success' : 'danger'} title={untaggedTitle(r.tagged && r.tagged.state, 'state')}>
            {r.state}
          </Badge>
        </td>
      </tr>
    );
  };

  const pinnedOutcome = result && result.results[pinned];

  /**
   * The regex list is a real sequence: the first regex that matches an e-mail wins, so the position is information and
   * is shown as the number the row is tried in. What tells the engineer whether a regex works is the count next to it,
   * so that is the only place in the row that carries colour.
   */
  const matchCountLabel = (index: number): string => {
    if (!messages.length || !result) {
      return '';
    }
    const matched = matchedBy(index);
    return matched ? `matches ${matched} of ${messages.length} e-mails` : `matches none of the ${messages.length} e-mails`;
  };

  const matchCountClass = (index: number): string => (matchedBy(index) ? 'text-success' : 'text-warning');

  /** Copy to the clipboard, confirming for a moment; the fallback keeps older browsers working. */
  const writeClipboard = (value: string, done: () => void) => {
    if (!value) {
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done, () => setError('Could not copy to the clipboard.'));
      return;
    }
    const area = document.createElement('textarea');
    area.value = value;
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    document.body.removeChild(area);
    done();
  };

  const copyRegex = (index: number) =>
    writeClipboard(regex[index], () => {
      setCopiedRegex(index);
      setTimeout(() => setCopiedRegex((current) => (current === index ? null : current)), 1500);
    });

  /**
   * The messages to hand to Claude. Near-identical e-mails teach it nothing, so one per shape comes first, with
   * digits flattened to build the signature, and the rest fill the remaining slots up to a readable ten.
   */
  const promptMessages = useMemo(() => {
    const seen = new Set<string>();
    const signatures = new Set<string>();
    const distinct: string[] = [];
    const rest: string[] = [];
    messages.forEach((message) => {
      const text = message.trim();
      if (!text || seen.has(text)) {
        return;
      }
      seen.add(text);
      const signature = text.replace(/\d+/g, '0').slice(0, 160);
      if (signatures.has(signature)) {
        rest.push(text);
      } else {
        signatures.add(signature);
        distinct.push(text);
      }
    });
    return distinct.concat(rest).slice(0, 10);
  }, [messages]);

  const claudePrompt = useMemo(
    () => `/mep-parser-regex ${[site, type].filter(Boolean).join(' ') || 'SITE PROVIDER'}\n\n${promptMessages.join('\n-----\n')}\n`,
    [site, type, promptMessages],
  );

  const copyPrompt = () =>
    writeClipboard(claudePrompt, () => {
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    });

  const REGEX_ROW_ACTION_STYLE = { width: 30, height: 30, lineHeight: 1 };

  /** Copy, reorder and remove, in one strip to the right of the regex itself. */
  const regexRowActions = (index: number) => [
    {
      key: 'copy',
      icon: copiedRegex === index ? <Icon.Check2 size={19} /> : <Icon.Clipboard size={17} />,
      title: copiedRegex === index ? 'copied' : 'copy this regex',
      className: copiedRegex === index ? 'text-success' : 'text-muted',
      disabled: !regex[index],
      run: () => copyRegex(index),
    },
    {
      key: 'up',
      icon: <Icon.ArrowUp size={19} />,
      title: 'try this regex earlier',
      className: 'text-muted',
      disabled: index === 0,
      run: () => moveRegex(index, -1),
    },
    {
      key: 'down',
      icon: <Icon.ArrowDown size={19} />,
      title: 'try this regex later',
      className: 'text-muted',
      disabled: index === regex.length - 1,
      run: () => moveRegex(index, 1),
    },
    {
      key: 'remove',
      icon: <Icon.Trash size={17} />,
      title: 'remove this regex',
      className: 'text-danger ml-1',
      disabled: false,
      run: () => removeRegex(index),
    },
  ];

  /**
   * One source of messages at a time. Loading is never automatic: a mailbox with nothing in it should say so when
   * asked, not leave an empty panel behind on open.
   */
  /**
   * What is wrong with a regex, if anything. A pattern that does not compile is rejected outright; one that compiles
   * but tags fewer than the three parts is the quiet failure this work exists to remove, so it is named too.
   */
  const regexProblem = (value: string): string | null => {
    const text = value.trim();
    if (!text) {
      return null;
    }
    try {
      new RegExp(text);
    } catch (e) {
      return `does not compile: ${e.message}`;
    }
    const missing = (['source', 'severity', 'state'] as RegexTag[]).filter((tag) => !text.includes(`(?<${tag}>`));
    return missing.length ? `no ${missing.join(' or ')} tagged, so the keywords would be searched in the whole e-mail` : null;
  };

  /** Bring the regex Claude wrote straight into the list, without closing the workspace to reach the plus button. */
  const addPastedRegex = () => {
    const value = pastedRegex.trim();
    if (!value) {
      return;
    }
    const problem = regexProblem(value);
    if (problem) {
      setError(`That regex ${problem}.`);
      return;
    }
    onRegexChange([...regex, value]);
    setAddedRegexAt(regex.length + 1);
    setPastedRegex('');
    setError(null);
    setTimeout(() => setAddedRegexAt(null), 4000);
  };

  /**
   * The line the received tab shows before anything is loaded: what will arrive and why it is worth loading, or that
   * the mailbox has been quiet. Built here rather than inline so a long sentence is prose, not JSX to be wrapped.
   */
  const pasteInvitation = 'One alert e-mail exactly as it arrived. To work on several, leave a blank line between them.';

  /** Nothing past the message itself appears until it is the one being worked on: pasted text has to be confirmed. */
  const readyToWork = !!messages.length && !(sourceMode === 'pasted' && editingPaste && !!pasted.trim());

  const messageCountLabel = messages.length === 1 ? 'the message' : `the ${messages.length} messages`;

  const receivedInvitation =
    loadedCount === 0
      ? `Nothing reached ${mailboxCode ? `…_${mailboxCode}@` : 'this mailbox'} in the last 90 days. Paste a message instead.`
      : 'The last 50 e-mails this Monitoring System received, exactly as they arrived, to build the regex against the real thing.';

  const messageSource = () => (
    <div className="mb-3">
      <Nav tabs className="nav-fill mb-3">
        <NavItem>
          <NavLink
            href="#"
            active={sourceMode === 'received'}
            onClick={(e) => {
              e.preventDefault();
              setSourceMode('received');
            }}
            data-testid="monitoringsystem-editor-source-received"
          >
            Received e-mails
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            href="#"
            active={sourceMode === 'pasted'}
            onClick={(e) => {
              e.preventDefault();
              setSourceMode('pasted');
            }}
            data-testid="monitoringsystem-editor-source-pasted"
          >
            Paste a message
          </NavLink>
        </NavItem>
      </Nav>
      {sourceMode === 'pasted' && !editingPaste && pasted.trim() ? null : sourceMode === 'received' ? (
        /*
         * With nothing loaded this panel is the whole tab, so it carries the invitation: the thing that will happen,
         * in a column narrow enough to read, centred rather than pinned to the left edge of a wide modal.
         */
        <div className={samples.length ? 'd-flex align-items-center flex-wrap' : 'text-center py-4'}>
          {samples.length ? null : <Icon.EnvelopeOpen size={30} className="text-muted mb-3" />}
          {samples.length ? null : (
            <div className={`mx-auto mb-3 ${error ? 'text-danger' : 'text-muted'}`} style={{ maxWidth: 560 }}>
              {error || receivedInvitation}
            </div>
          )}
          <Button
            size="sm"
            color="primary"
            className={samples.length ? 'mr-3' : ''}
            onClick={loadSamples}
            disabled={!monitoringSystemId || loadingSamples}
            data-testid="monitoringsystem-editor-load-received"
          >
            {loadingSamples ? 'Loading…' : samples.length ? 'Load them again' : 'Load the last 50 received'}
          </Button>
          {samples.length ? <span className="small text-muted">{samples.length} loaded, newest first.</span> : null}
        </div>
      ) : (
        /*
         * Same shape as the received tab: with nothing pasted the panel is an invitation, centred and narrow enough
         * to read. Once there is text the icon and the line step aside and the box is the only thing left.
         */
        <div className={pasted.trim() ? 'mt-2' : 'text-center py-4'}>
          {pasted.trim() ? null : (
            <>
              <Icon.Clipboard size={30} className="text-muted mb-3" />
              <div className="mx-auto mb-3 text-muted" style={{ maxWidth: 420 }}>
                {pasteInvitation}
              </div>
            </>
          )}
          <div className="position-relative mx-auto text-left" style={{ maxWidth: 760 }}>
            <Input
              type="textarea"
              bsSize="sm"
              rows={4}
              className="text-monospace"
              style={{ resize: 'none', padding: '12px 14px 46px', fontSize: 12, lineHeight: 1.6 }}
              placeholder="Paste the e-mail here"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              data-testid="monitoringsystem-editor-sandbox-paste"
            />
            {/* Only once there is something to confirm. An empty field has nothing to use. */}
            {pasted.trim() ? (
              <Button
                size="sm"
                color="primary"
                className="position-absolute d-inline-flex align-items-center"
                style={{ right: 8, bottom: 8 }}
                onClick={() => setEditingPaste(false)}
                title="Work on this message"
                data-testid="monitoringsystem-editor-paste-confirm"
              >
                <Icon.CheckLg size={16} className="mr-1" />
                Use this message
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="parsing-sandbox">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2 mt-3">
        <span>
          Regex <small className="text-muted">(optional; tried in order, the first that matches wins)</small>
        </span>
        <span className="d-flex align-items-center">
          <Button size="sm" color="secondary" outline className="mr-2" onClick={() => setShowWorkspace(true)}>
            Build from a message
          </Button>
          <Button
            close={false}
            color="link"
            className="btn-icon-flat d-inline-flex align-items-center justify-content-center p-0"
            style={{ width: 30, height: 30, lineHeight: 1 }}
            onClick={() => onRegexChange([...regex, ''])}
            disabled={regex.some((r) => !r.trim())}
            title={regex.some((r) => !r.trim()) ? 'Fill the empty regex first' : 'Add a regex'}
            data-testid="monitoringsystem-editor-regex-add"
          >
            <Icon.PlusLg size={18} />
          </Button>
        </span>
      </div>

      {regex.map((rx, i) => (
        <div key={i} className="mb-2">
          <div className="d-flex align-items-center">
            <span
              className="d-inline-flex align-items-center justify-content-center text-white mr-2"
              style={{ width: 22, height: 22, borderRadius: '50%', background: '#66615b', fontSize: 12, flex: '0 0 22px' }}
              title={`tried in position ${i + 1} of ${regex.length}`}
            >
              {i + 1}
            </span>
            <Input
              type="text"
              bsSize="sm"
              className="text-monospace"
              placeholder="Type or paste a regex that tags the source, the severity and the state"
              value={rx}
              onChange={(e) => updateRegex(i, e.target.value)}
              data-testid={`monitoringsystem-editor-regex-${i}`}
            />
            <span className="d-flex align-items-center ml-1">
              {regexRowActions(i).map((action) => (
                <Button
                  key={action.key}
                  close={false}
                  color="link"
                  className={`btn-icon-flat d-inline-flex align-items-center justify-content-center p-0 ${action.className}`}
                  style={REGEX_ROW_ACTION_STYLE}
                  onClick={action.run}
                  disabled={action.disabled}
                  title={action.title}
                  data-testid={`monitoringsystem-editor-regex-${action.key}-${i}`}
                >
                  {action.icon}
                </Button>
              ))}
            </span>
          </div>
          {regexProblem(rx) ? (
            <div className="small mt-1 text-danger" style={{ marginLeft: 30 }}>
              {regexProblem(rx)}
            </div>
          ) : matchCountLabel(i) ? (
            <div className={`small mt-1 ${matchCountClass(i)}`} style={{ marginLeft: 30 }}>
              {matchCountLabel(i)}
            </div>
          ) : null}
        </div>
      ))}

      <small className="form-text text-muted">
        Tag the equipment with <code>(?&lt;source&gt;…)</code> and the severity and state tokens with <code>(?&lt;severity&gt;…)</code> and{' '}
        <code>(?&lt;state&gt;…)</code>. The keyword boxes above are then searched inside what each tag captured.
      </small>

      {/*
       * Everything that needs example e-mails lives here, out of the editor. The editor keeps the list that gets saved
       * and nothing else; loading messages, tagging one, asking Claude and testing all happen in this one workspace,
       * so no piece of it sits on screen while it is not being used.
       */}
      <Modal isOpen={showWorkspace} toggle={() => setShowWorkspace(false)} size="xl" scrollable zIndex={1060}>
        <ModalHeader toggle={() => setShowWorkspace(false)}>Build a regex from a message</ModalHeader>
        <ModalBody className="d-flex flex-column" style={{ padding: '1rem 1.25rem', overflow: 'hidden' }}>
          <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
            {/* Said once, at the top: it governs both ways of getting a regex, so it belongs to neither of them. */}
            <div className="text-muted mb-4">
              A regex is optional: with the keywords alone the source comes from the old extraction. When you add one it has to tag the three parts
              MEP needs, <code>(?&lt;source&gt;…)</code> around the equipment, and <code>(?&lt;severity&gt;…)</code> and{' '}
              <code>(?&lt;state&gt;…)</code> around the tokens the keyword boxes are searched in. Tag them by hand below, or ask Claude and paste its
              answer back.
            </div>
            {messageSource()}

            {builder && readyToWork ? (
              <>
                <div className="d-flex align-items-center mb-3">
                  <Icon.CursorText size={18} className="text-muted mr-2" />
                  <span>Tag it yourself</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <Button
                    close={false}
                    color="link"
                    size="sm"
                    className="btn-icon-flat p-0 mr-2 text-muted"
                    disabled={pinned === 0}
                    onClick={() => setPinned(pinned - 1)}
                    title="previous message"
                  >
                    <Icon.ChevronLeft size={16} />
                  </Button>
                  <span className="text-muted">
                    Message {pinned + 1} of {messages.length}
                  </span>
                  <Button
                    close={false}
                    color="link"
                    size="sm"
                    className="btn-icon-flat p-0 ml-2 text-muted"
                    disabled={pinned >= messages.length - 1}
                    onClick={() => setPinned(pinned + 1)}
                    title="next message"
                  >
                    <Icon.ChevronRight size={16} />
                  </Button>
                  {sourceMode === 'pasted' ? (
                    <Button
                      close={false}
                      color="link"
                      className="btn-icon-flat text-muted p-0 ml-2 d-inline-flex align-items-center justify-content-center"
                      style={{ width: 22, height: 22 }}
                      onClick={() => setEditingPaste(true)}
                      title="Change the message"
                      aria-label="Change the message"
                      data-testid="monitoringsystem-editor-paste-edit"
                    >
                      <Icon.Pencil size={14} />
                    </Button>
                  ) : null}
                  <span className="ml-auto">
                    {existingMatch >= 0 ? (
                      <Badge color="success" title="this message is already covered; a new regex for it would never be reached">
                        regex {existingMatch + 1} already matches this one
                      </Badge>
                    ) : (
                      <span className="text-muted">no regex in the list matches this one yet</span>
                    )}
                  </span>
                </div>
                <div
                  ref={textRef}
                  onMouseUp={onMouseUp}
                  className="text-monospace bg-light rounded"
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontSize: 12,
                    lineHeight: 1.9,
                    userSelect: 'text',
                    cursor: 'text',
                    padding: '14px 16px',
                    maxHeight: 220,
                    overflowY: 'auto',
                  }}
                >
                  {renderHighlighted(
                    pinnedMessage,
                    pinnedSpans
                      .map((s): { start: number; end: number; color: string; title: string } => ({
                        start: s.start,
                        end: s.end,
                        color: TAG_COLORS[s.tag],
                        title: s.tag,
                      }))
                      .concat(
                        builder.pending
                          ? [{ start: builder.pending.start, end: builder.pending.end, color: PENDING_COLOR, title: 'selected, not tagged yet' }]
                          : [],
                      ),
                  )}
                </div>

                {builder.pending ? (
                  <div className="d-flex align-items-center flex-wrap mt-2 small">
                    <span className="text-muted mr-3">
                      Selected <code>{builder.pending.text}</code>, it is the
                    </span>
                    <Button color="secondary" outline size="sm" className="mr-2" onClick={() => tagPending('source')}>
                      source
                    </Button>
                    <Button color="secondary" outline size="sm" className="mr-2" onClick={() => tagPending('severity')}>
                      severity
                    </Button>
                    <Button color="secondary" outline size="sm" className="mr-3" onClick={() => tagPending('state')}>
                      state
                    </Button>
                    <Button
                      close={false}
                      color="link"
                      className="btn-icon-flat text-muted p-0 d-inline-flex align-items-center justify-content-center"
                      style={{ width: 24, height: 24 }}
                      onClick={() => setBuilder({ ...builder, pending: null })}
                      title="cancel this selection"
                    >
                      <Icon.X size={18} />
                    </Button>
                  </div>
                ) : (
                  <div className="text-muted mt-2">
                    {pinnedSpans.length ? (
                      <>
                        Tagged here:{' '}
                        {pinnedSpans
                          .slice()
                          .sort((a, b) => a.start - b.start)
                          .map((s) => (
                            <mark
                              key={s.tag}
                              style={{ background: TAG_COLORS[s.tag], padding: '0 4px', marginRight: 4 }}
                              title={pinnedMessage.slice(s.start, s.end)}
                            >
                              {s.tag}{' '}
                              <a
                                href="#"
                                className="text-danger"
                                onClick={(e) => {
                                  e.preventDefault();
                                  untag(s.tag);
                                }}
                                title="remove this tag"
                              >
                                ×
                              </a>
                            </mark>
                          ))}
                      </>
                    ) : (
                      `Select the ${nextTagToSelect} in the message above.`
                    )}
                    {exampleIndexes.length > 1 ? <span> · messages used: {exampleIndexes.map((k) => `#${k + 1}`).join(', ')}</span> : null}
                  </div>
                )}

                {draft && draft.regex ? (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>The regex this makes</span>
                      {draftMatches ? (
                        <Badge color={draftMatches.length === messages.length ? 'success' : 'warning'}>
                          matches {draftMatches.length} of {messages.length}
                        </Badge>
                      ) : null}
                    </div>
                    <pre className="bg-light border rounded p-2 mb-1" style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>
                      {draft.regex}
                    </pre>
                    {missingDraftTags.length ? (
                      <div className="text-warning mb-2">
                        Still to tag: {missingDraftTags.join(', ')}. All three are needed before this regex can be added.
                      </div>
                    ) : null}
                    {draft.problems.length ? <div className="text-danger mb-2">{draft.problems.join('; ')}</div> : null}
                    {draftMatches && draftMatches.length < messages.length && !draft.problems.length ? (
                      <div className="text-warning mb-2">
                        Not matched yet:{' '}
                        {messages
                          .map((_, k) => k)
                          .filter((k) => !draftMatches.includes(k))
                          .map((k) => `#${k + 1}`)
                          .join(', ')}
                        . Open one of them and tag the same parts; the examples merge into one regex.
                      </div>
                    ) : null}
                    <Button size="sm" color="primary" onClick={addDraft} disabled={!!draft.problems.length || !!missingDraftTags.length}>
                      Add to the list
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}

            {readyToWork ? (
              <div className="mt-4 pt-3 border-top">
                <div className="d-flex align-items-center mb-3">
                  <Icon.Stars size={18} className="text-muted mr-2" />
                  <span>Or ask Claude</span>
                  <span className="text-muted ml-2">
                    in a chat with the <code>mep-parser-regex</code> skill
                  </span>
                  <Button
                    size="sm"
                    color={copiedPrompt ? 'success' : 'secondary'}
                    outline={!copiedPrompt}
                    className="ml-auto flex-shrink-0"
                    onClick={copyPrompt}
                    title={`Copies ${promptMessages.length} of the messages, with the site and the provider`}
                    data-testid="monitoringsystem-editor-copy-prompt"
                  >
                    {copiedPrompt ? 'Copied' : 'Copy for Claude'}
                  </Button>
                </div>
                <div className="d-flex align-items-center">
                  <Input
                    bsSize="sm"
                    className="text-monospace"
                    placeholder="Paste the regex Claude wrote"
                    value={pastedRegex}
                    onChange={(e) => {
                      setPastedRegex(e.target.value);
                      setError(null);
                    }}
                    onKeyPress={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPastedRegex();
                      }
                    }}
                    data-testid="monitoringsystem-editor-paste-regex"
                  />
                  <Button
                    size="sm"
                    color="primary"
                    className="ml-2 flex-shrink-0 text-nowrap"
                    disabled={!pastedRegex.trim()}
                    onClick={addPastedRegex}
                  >
                    Add to the list
                  </Button>
                </div>
                {/* The reason a regex was refused belongs here, next to the field, not inside the test modal. */}
                {error ? <div className="text-danger mt-2">{error}</div> : null}
                {addedRegexAt ? <div className="text-success mt-2">Added to the list as regex {addedRegexAt}.</div> : null}
              </div>
            ) : null}

            {/* Testable with or without a regex: without one it shows what the old source extraction produces. */}
            {readyToWork ? (
              <div className="d-flex justify-content-end align-items-center mt-4 pt-3 border-top">
                <span className="small text-muted mr-3">Run it over {messageCountLabel}</span>
                <Button size="sm" color="primary" onClick={() => setShowTest(true)} data-testid="monitoringsystem-editor-test-parser">
                  Test parser
                </Button>
              </div>
            ) : null}

            {showTest ? (
              <>
                {result && result.errors.length ? (
                  <BsAlert color="danger" className="py-1 small">
                    {result.errors.join('; ')}
                  </BsAlert>
                ) : null}
                {result && result.warnings.length ? (
                  <BsAlert color="warning" className="py-1 small">
                    {result.warnings.join('; ')}
                  </BsAlert>
                ) : null}
                {!messages.length ? (
                  <div className="small text-muted">Nothing to test yet.</div>
                ) : running || !result ? (
                  <div className="small text-muted">Running the parser over {messages.length} messages…</div>
                ) : (
                  <>
                    <BsAlert color={result.failed ? 'warning' : 'success'} className="py-2 mb-3 small">
                      <strong>
                        {messages.length} e-mails → {result.alerts} alerts
                      </strong>
                      {result.ignored ? <span> · {result.ignored} ignored, no alert</span> : null}
                      {result.failed ? <span> · {result.failed} that no regex matched, kept in Notifications Status</span> : null}
                    </BsAlert>
                    <Table size="sm" hover className="small mb-3" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 34 }} />
                        <col style={{ width: 92 }} />
                        <col />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 96 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Result</th>
                          <th>Source</th>
                          <th>Sev</th>
                          <th>State</th>
                        </tr>
                      </thead>
                      <tbody>{result.results.map(outcomeRow)}</tbody>
                    </Table>
                    {pinnedOutcome ? (
                      <div className="card">
                        <div className="card-header py-1 small d-flex justify-content-between">
                          <span>Message {pinned + 1}: what the regex captured</span>
                          <span>
                            <mark style={{ background: TAG_COLORS.source }}>source</mark>{' '}
                            <mark style={{ background: TAG_COLORS.severity }}>severity</mark>{' '}
                            <mark style={{ background: TAG_COLORS.state }}>state</mark>
                          </span>
                        </div>
                        <div className="card-body py-2 small text-monospace" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                          {renderHighlighted(pinnedOutcome.message, capturedSpans(pinnedOutcome))}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowWorkspace(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ParsingSandbox;
